import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";
import { useFormik } from "formik";
import * as Yup from "yup";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import AuthLayout from "../components/layout/AuthLayout";
import PasswordInput from "../components/PasswordInput";
import { Checkbox, Input, Button, OTPInput, ReCaptcha } from "../components/ui";
import SEO from "../components/SEO";

const Login = () => {
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const recaptchaRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, verifyDevice, resendDeviceOTP } = useAuth();

  // Countdown timer for resend OTP
  useEffect(() => {
    let interval = null;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => (prev <= 1 ? 0 : prev - 1));
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleResendOTP = async () => {
    try {
      await resendDeviceOTP(formik.values.email);
      setResendTimer(60);
      toast.success("OTP resent to your email");
    } catch (error) {
      toast.error(error.message || "Failed to resend OTP");
    }
  };

  const getLoginSchema = (isOtpSent) =>
    Yup.object().shape({
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      password: isOtpSent
        ? Yup.string()
        : Yup.string().required("Password is required"),
      otp: isOtpSent ? Yup.string().required("OTP is required") : Yup.string(),
      recaptchaToken: isOtpSent
        ? Yup.string()
        : Yup.string().required("Please complete the reCAPTCHA"),
    });

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      otp: "",
      recaptchaToken: "",
      keepLoggedIn: false,
      isOtpSent: false,
    },
    validationSchema: getLoginSchema(isOtpSent),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (!isOtpSent) {
          const response = await login(
            values.email,
            values.password,
            values.recaptchaToken,
          );
          if (response.requiresOTP) {
            setIsOtpSent(true);
            setResendTimer(60);
            toast.success("OTP sent to your email");
          } else {
            toast.success("Login successful");
            const from = location.state?.from?.pathname || "/dashboard";
            navigate(from);
          }
        } else {
          await verifyDevice(values.email, values.otp);
          toast.success("Device verified successfully");
          const from = location.state?.from?.pathname || "/dashboard";
          navigate(from);
        }
      } catch (error) {
        recaptchaRef.current?.reset();
        formik.setFieldValue("recaptchaToken", "");
        formik.setFieldError(
          "formError",
          error.response?.data?.message ||
            error.message ||
            (isOtpSent ? "Failed to verify device" : "Failed to login"),
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <AuthLayout>
      <SEO index="login" />
      <div className="space-y-8">
        <div>
          <h2 className="font-heading text-4xl font-bold text-gray-900">
            Sign in
          </h2>
          <p className="text-sm text-gray-500 mt-3">
            Enter your email and password to sign in!
          </p>
        </div>

        <form
          onSubmit={(e) => {
            formik.handleSubmit(e);
            e.preventDefault();
          }}
          className="space-y-5"
        >
          <Input
            id="email"
            name="email"
            type="email"
            label="Email"
            required
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            placeholder="mail@example.com"
            disabled={isOtpSent}
            error={
              formik.errors.email && formik.touched.email
                ? formik.errors.email
                : undefined
            }
          />
          {!isOtpSent && (
            <div>
              <label
                htmlFor="password"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Password <span className="text-red-500">*</span>
              </label>
              <PasswordInput
                id="password"
                name="password"
                autoComplete="current-password"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.password}
                placeholder="Min. 8 characters"
                error={
                  formik.errors.password && formik.touched.password
                    ? formik.errors.password
                    : undefined
                }
              />
            </div>
          )}
          {isOtpSent && (
            <div className="animate-fadeIn">
              <OTPInput
                name="otp"
                value={formik.values.otp}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={
                  formik.errors.otp && formik.touched.otp
                    ? formik.errors.otp
                    : undefined
                }
                resendTimer={resendTimer}
                onResend={handleResendOTP}
              />
            </div>
          )}
          {formik.errors.formError && (
            <div className="text-red-500 text-xs bg-red-50 border border-red-200 p-3 rounded-xl flex items-center gap-2">
              <InformationCircleIcon className="h-4 w-4 shrink-0" />
              <span>{formik.errors.formError}</span>
            </div>
          )}

          {!isOtpSent && (
            <ReCaptcha
              ref={recaptchaRef}
              onChange={(token) => {
                formik.setFieldValue("recaptchaToken", token);
                formik.setFieldTouched("recaptchaToken", true, false);
              }}
              error={
                formik.errors.recaptchaToken && formik.touched.recaptchaToken
                  ? formik.errors.recaptchaToken
                  : undefined
              }
            />
          )}

          {/* Keep me logged in + Forgot Password */}
          {!isOtpSent && (
            <div className="flex items-center justify-between">
              <Checkbox
                id="keepLoggedIn"
                name="keepLoggedIn"
                checked={formik.values.keepLoggedIn}
                onChange={formik.handleChange}
                label="Keep me logged in"
              />
              <Link
                to="/forgot-password"
                className="text-sm font-medium text-primary hover:text-primary-dark transition-colors"
              >
                Forgot Password?
              </Link>
            </div>
          )}

          <Button
            type="submit"
            fullWidth
            loading={formik.isSubmitting}
            disabled={formik.isSubmitting}
            className="py-3"
          >
            {isOtpSent ? "Verify Device" : "Sign in"}
          </Button>

          <p className="text-center text-sm text-gray-500">
            Not registered yet?{" "}
            <Link
              to="/signup"
              className="font-medium text-primary hover:text-primary-dark"
            >
              Create an account
            </Link>
          </p>
        </form>
      </div>
    </AuthLayout>
  );
};

export default Login;
